// pages/order/orderHistory/orderHistoryDetail/OrderHistoryDetail.tsx
import React, { useState, useEffect } from 'react';
import { Col, Row, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import OrderDetails from './subviews/orderDetailSection/OrderDetails';
import AddressAndServiceDetailSection from './subviews/addressAndServiceDetailSection/AddressAndServiceDetailSection';
import './OrderHistoryDetail.scss';
import OrderStatusSection from './subviews/orderStatusSection/OrderStatusSection';
import ContactSection from './subviews/contactSection/ContactSection';
import { buildOrderHistoryListPath } from 'utils/pathBuilder';
import { fetchOrderDetails, fetchOrderStatusHistory } from 'services/historyService';
import type {
  HistoryOrderDetailsEntity,
  OrderStatusLog,
} from 'types/dto/order';
import FooterButtonGroup from 'components/footerButtonGroup/FooterButtonGroup';
// import { useSession } from 'context/SessionContext';
import { MemberProfile } from 'types/dto/profile';

const OrderHistoryDetail: React.FC = () => {
  const { t, i18n } = useTranslation('order_history');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { id: orderId } = useParams<{ id: string }>();

  const [orderItemsExpanded, setOrderItemsExpanded] = useState<boolean>(false);
  const [voucherExpanded, setVoucherExpanded] = useState(false);
  const [orderDetailData, setOrderDetailData] =
    useState<HistoryOrderDetailsEntity | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [statusHistory, setStatusHistory] = useState<OrderStatusLog[]>([]);
  // const {session} = useSession();
  const { profile } = useOutletContext<{ profile: MemberProfile | null }>();

  useEffect(() => {
    const uniqueId = profile?.loyalty_account?.unique_id;

    // Guard: `profile` is loaded asynchronously by the Layout Outlet. On a
    // deep-link / page refresh (e.g. typing /zh/order/order-history/30076
    // directly), this child mounts BEFORE profile resolves, so uniqueId is
    // briefly undefined. Skip the fetch until it's available — otherwise we
    // send juicyAppUniqueId="" and the API returns HTTP 500. Once profile
    // resolves the dependency below changes and the effect re-runs with the
    // real id, firing exactly one valid request.
    if (!uniqueId || !orderId) {
      return;
    }

    const fetchAllData = async () => {

      try {
        setIsLoading(true);
        setLoadError(false);

        // Fetch order details
        const response:any = await fetchOrderDetails('/history', {
          juicyAppUniqueId: uniqueId,
          orderId,
          lang: i18n.language,
        });

        // check format of response
        if (!response.orderID) {
          throw new Error('Invalid response: missing orderID');
        }

        const detailResponse = {
          ...response,
          oph_sts: response.ophSts,
          collectionStartDate: response.collectionStartTime,
          deliveryStartDate: response.deliveryStartTime,
          collectionEndDate: response.collectionEndTime,
          deliveryEndDate: response.deliveryEndTime,
          redeemableTokens: response.loyaltyItems || [],
          tickets: (response.tickets || []).map((ticket: any) => ({ ...ticket })),
        };

        setOrderDetailData(detailResponse);

        // Fetch order status history
        // Orders migrated from legacy have no status history (API returns no historyList),
        // so a missing or failed history must not hide the order details
        try {
          const historyResponse = await fetchOrderStatusHistory('/history', {
            juicyAppUniqueId: uniqueId,
            orderId,
            lang: i18n.language,
          });

          const statusHistory: OrderStatusLog[] =
            historyResponse?.data?.historyList ?? [];
          const sortedHistory: OrderStatusLog[] = statusHistory.sort(
            (a, b) =>
              new Date(b.changeTime).getTime() - new Date(a.changeTime).getTime()
          );

          setStatusHistory(sortedHistory);
        } catch (historyError) {
          setStatusHistory([]);
        }
      } catch (error) {
        setLoadError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [i18n.language, orderId, profile?.loyalty_account?.unique_id]);

  const handleBack = () => {
    navigate(buildOrderHistoryListPath());
  };

  const toggleOrderItems = () => {
    setOrderItemsExpanded(!orderItemsExpanded);
  };

  const toggleVoucher = () => {
    setVoucherExpanded(!voucherExpanded);
  };

  const handleContactClick = () => {
    window.open('https://wa.me/85295416079', '_blank');
  };

  return (
    <div className="order-history-detail-container">
      <div className="order-history-detail-content">
        {/* Show loading skeleton overlay while data is loading */}
        {isLoading && (
          <div className="skeleton-card">
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
        )}

        {/* Show error alert if loading failed */}
        {!isLoading && loadError && (
          <div className="load-data-error">
            {tCommon('error.loadDataError')}
          </div>
        )}

        {!isLoading && !loadError && orderDetailData && (
          <>
            <Row gutter={16}>
              <Col
                xs={{
                  order: 2,
                  span: 24,
                }}
                md={{
                  order: 1,
                  span: 16,
                }}
              >
                <OrderDetails
                  orderData={orderDetailData}
                  orderItemsExpanded={orderItemsExpanded}
                  onToggleOrderItems={toggleOrderItems}
                  voucherExpanded={voucherExpanded}
                  onToggleVoucher={toggleVoucher}
                />
                <AddressAndServiceDetailSection orderData={orderDetailData} />
              </Col>

              <Col
                xs={{
                  order: 1,
                  span: 24,
                }}
                md={{
                  order: 2,
                  span: 8,
                }}
                className="right-column"
              >
                <ContactSection onContactClick={handleContactClick} />
                <OrderStatusSection statusHistory={statusHistory} />
              </Col>
            </Row>

            <FooterButtonGroup
              continueText={t('back')}
              onContinue={handleBack}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default OrderHistoryDetail;
